package com.tixforgigs.common.ui.components.mandarin

import com.tixforgigs.apax.models.Amount
import com.tixforgigs.common.core.enums.MandarinInputType
import com.tixforgigs.common.core.extensions.toPriceString
import com.tixforgigs.common.core.models.ui.InputState
import com.tixforgigs.common.core.util.DateTimeUtil
import com.tixforgigs.common.core.util.DateTimeUtil.currentInstant
import com.tixforgigs.common.core.util.DateTimeUtil.roundOffToMinute
import com.tixforgigs.common.core.util.DateTimeUtil.toDateTimeString
import com.tixforgigs.common.core.util.DateTimeUtil.toInputValue
import com.tixforgigs.common.core.util.InputUtil.emailRegex
import com.tixforgigs.common.core.util.InputUtil.toFormattedIBANString
import com.tixforgigs.tarami.AraiMessage.inputDatetimeResaleEndLabel
import com.tixforgigs.tarami.AraiMessage.inputResaleEndDescription
import com.tixforgigs.tarami.AraiMessage.inputResaleStartLabel
import com.tixforgigs.tarami.AraiMessage.inputTextVoucherLabel
import com.tixforgigs.tarami.AraiMessage.inputTextVoucherPlaceholder
import com.tixforgigs.tarami.GlobalMessage.inputDateBirthdateErrorEmpty
import com.tixforgigs.tarami.GlobalMessage.inputDateBirthdateLabel
import com.tixforgigs.tarami.GlobalMessage.inputDateBirthdatePlaceholder
import com.tixforgigs.tarami.GlobalMessage.inputResaleStartDescription
import com.tixforgigs.tarami.GlobalMessage.inputTextAcademicLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextAddressAdditionLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextBICLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextBankAccountOwnerLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextBankLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextCityErrorEmpty
import com.tixforgigs.tarami.GlobalMessage.inputTextCityLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextCityPlaceholder
import com.tixforgigs.tarami.GlobalMessage.inputTextCompanyNameLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextCompanyNamePlaceholder
import com.tixforgigs.tarami.GlobalMessage.inputTextDisplayNameLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextEmailErrorEmpty
import com.tixforgigs.tarami.GlobalMessage.inputTextEmailErrorRegex
import com.tixforgigs.tarami.GlobalMessage.inputTextEmailLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextEmailPlaceholder
import com.tixforgigs.tarami.GlobalMessage.inputTextFirstNameErrorEmpty
import com.tixforgigs.tarami.GlobalMessage.inputTextFirstNameLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextFirstNamePlaceholder
import com.tixforgigs.tarami.GlobalMessage.inputTextHouseNumberErrorEmpty
import com.tixforgigs.tarami.GlobalMessage.inputTextHouseNumberLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextHouseNumberPlaceholder
import com.tixforgigs.tarami.GlobalMessage.inputTextIBANLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextLastNameErrorEmpty
import com.tixforgigs.tarami.GlobalMessage.inputTextLastNameLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextLastNamePlaceholder
import com.tixforgigs.tarami.GlobalMessage.inputTextPasswordErrorEmpty
import com.tixforgigs.tarami.GlobalMessage.inputTextPasswordLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextPayeeLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextPaymentAmountLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextPaymentReasonLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextPostalCodeErrorEmpty
import com.tixforgigs.tarami.GlobalMessage.inputTextPostalCodeLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextPostalCodePlaceholder
import com.tixforgigs.tarami.GlobalMessage.inputTextStreetErrorEmpty
import com.tixforgigs.tarami.GlobalMessage.inputTextStreetLabel
import com.tixforgigs.tarami.GlobalMessage.inputTextStreetPlaceholder
import io.kvision.core.Container
import io.kvision.event.changeFlow
import io.kvision.event.clickFlow
import io.kvision.event.eventFlow
import io.kvision.event.inputFlow
import io.kvision.html.Autocomplete
import io.kvision.material.ExperimentalMaterialApi
import io.kvision.material.icon.leadingIcon
import io.kvision.material.icon.trailingIcon
import io.kvision.material.textfield.TextFieldInputMode
import io.kvision.material.textfield.TextFieldInputType
import io.kvision.material.textfield.TextFieldRangeConstraint
import io.kvision.material.textfield.outlinedTextField
import io.kvision.state.bind
import io.kvision.utils.perc
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

@OptIn(ExperimentalMaterialApi::class, ExperimentalTime::class)
fun <S> Container.mandarinInput(
  state: MutableStateFlow<InputState<S>>,
  inputType: MandarinInputType,
  leadingIcon: Pair<String, suspend () -> Unit>? = null,
  trailingIcon: Pair<String, suspend () -> Unit>? = null,
  isEnabled: StateFlow<Boolean> = MutableStateFlow(true),
  required: Boolean = true,
  readonly: Boolean = false,
  maxInput: Instant? = null,
  titleForGeneric: String? = null
) {
  val textField = outlinedTextField(
    label = when (inputType) {
      MandarinInputType.ACADEMIC_TITLE -> inputTextAcademicLabel()
      MandarinInputType.FIRST_NAME -> inputTextFirstNameLabel()
      MandarinInputType.LAST_NAME -> inputTextLastNameLabel()
      MandarinInputType.DISPLAY_NAME -> inputTextDisplayNameLabel()
      MandarinInputType.EMAIL -> inputTextEmailLabel()
      MandarinInputType.PASSWORD -> inputTextPasswordLabel()
      MandarinInputType.BIRTHDATE -> inputDateBirthdateLabel()
      MandarinInputType.BANK_NAME -> inputTextBankLabel()
      MandarinInputType.BANK_ACCOUNT_OWNER -> inputTextBankAccountOwnerLabel()
      MandarinInputType.BANK_ACCOUNT_IBAN -> inputTextIBANLabel()
      MandarinInputType.BANK_ACCOUNT_BIC -> inputTextBICLabel()
      MandarinInputType.PREPAYMENT_PAYEE_NAME -> inputTextPayeeLabel()
      MandarinInputType.PREPAYMENT_PAYEE_IBAN -> inputTextIBANLabel()
      MandarinInputType.PREPAYMENT_AMOUNT -> inputTextPaymentAmountLabel()
      MandarinInputType.PREPAYMENT_REASON -> inputTextPaymentReasonLabel()
      MandarinInputType.COMPANY_NAME -> inputTextCompanyNameLabel()
      MandarinInputType.STREET -> inputTextStreetLabel()
      MandarinInputType.HOUSE_NUMBER -> inputTextHouseNumberLabel()
      MandarinInputType.ADDRESS_ADDITION -> inputTextAddressAdditionLabel()
      MandarinInputType.POSTAL_CODE -> inputTextPostalCodeLabel()
      MandarinInputType.CITY -> inputTextCityLabel()
      MandarinInputType.RESALE_START -> inputResaleStartLabel()
      MandarinInputType.RESALE_END -> inputDatetimeResaleEndLabel()
      MandarinInputType.VOUCHER -> inputTextVoucherLabel()
      MandarinInputType.GENERIC -> titleForGeneric.orEmpty()
    },
    supportingText = when (inputType) {
      MandarinInputType.RESALE_START -> inputResaleStartDescription()
      MandarinInputType.RESALE_END -> inputResaleEndDescription(maxInput?.toDateTimeString().orEmpty())
      else -> null
    },
    placeholder = when (inputType) {
      MandarinInputType.FIRST_NAME -> inputTextFirstNamePlaceholder()
      MandarinInputType.LAST_NAME -> inputTextLastNamePlaceholder()
      MandarinInputType.EMAIL -> inputTextEmailPlaceholder()
      MandarinInputType.BIRTHDATE -> inputDateBirthdatePlaceholder()
      MandarinInputType.COMPANY_NAME -> inputTextCompanyNamePlaceholder()
      MandarinInputType.STREET -> inputTextStreetPlaceholder()
      MandarinInputType.HOUSE_NUMBER -> inputTextHouseNumberPlaceholder()
      MandarinInputType.POSTAL_CODE -> inputTextPostalCodePlaceholder()
      MandarinInputType.CITY -> inputTextCityPlaceholder()
      MandarinInputType.VOUCHER -> inputTextVoucherPlaceholder()
      else -> null
    },
    type = when (inputType) {
      MandarinInputType.PASSWORD -> TextFieldInputType.Password
      MandarinInputType.BIRTHDATE -> TextFieldInputType.Date
      MandarinInputType.RESALE_START, MandarinInputType.RESALE_END -> TextFieldInputType.DatetimeLocal
      else -> TextFieldInputType.Text
    },
    inputMode = when (inputType) {
      MandarinInputType.EMAIL -> TextFieldInputMode.Email
      MandarinInputType.BIRTHDATE, MandarinInputType.RESALE_START, MandarinInputType.RESALE_END -> TextFieldInputMode.Numeric
      else -> TextFieldInputMode.Text
    },
    min = when (inputType) {
      MandarinInputType.RESALE_START -> TextFieldRangeConstraint(
        currentInstant.roundOffToMinute().toInputValue(type = DateTimeUtil.Type.DATETIME)
      )

      else -> null
    },
    max = maxInput?.let { TextFieldRangeConstraint(it.toInputValue(DateTimeUtil.Type.DATETIME)) },
    autoComplete = when (inputType) {
      MandarinInputType.COMPANY_NAME -> Autocomplete.ORGANIZATION
      MandarinInputType.FIRST_NAME -> Autocomplete.GIVEN_NAME
      MandarinInputType.LAST_NAME -> Autocomplete.FAMILY_NAME
      MandarinInputType.DISPLAY_NAME -> Autocomplete.NICKNAME
      MandarinInputType.EMAIL -> Autocomplete.USERNAME
      MandarinInputType.PASSWORD -> Autocomplete.CURRENT_PASSWORD
      MandarinInputType.BIRTHDATE -> Autocomplete.BDAY
//      MandarinInputType.DOB_DAY -> Autocomplete.BDAY_DAY
//      MandarinInputType.DOB_MONTH -> Autocomplete.BDAY_MONTH
//      MandarinInputType.DOB_YEAR -> Autocomplete.BDAY_YEAR
      MandarinInputType.STREET -> Autocomplete.ADDRESS_LINE1
      MandarinInputType.ADDRESS_ADDITION -> Autocomplete.ADDRESS_LINE2
      MandarinInputType.POSTAL_CODE -> Autocomplete.POSTAL_CODE
      MandarinInputType.CITY -> Autocomplete.ADDRESS_LEVEL2
      else -> null
    },
    errorText = when (inputType) {
      MandarinInputType.FIRST_NAME -> inputTextFirstNameErrorEmpty()
      MandarinInputType.LAST_NAME -> inputTextLastNameErrorEmpty()
      MandarinInputType.EMAIL -> inputTextEmailErrorEmpty()
      MandarinInputType.PASSWORD -> inputTextPasswordErrorEmpty()
      MandarinInputType.BIRTHDATE -> inputDateBirthdateErrorEmpty()
      MandarinInputType.STREET -> inputTextStreetErrorEmpty()
      MandarinInputType.HOUSE_NUMBER -> inputTextHouseNumberErrorEmpty()
      MandarinInputType.POSTAL_CODE -> inputTextPostalCodeErrorEmpty()
      MandarinInputType.CITY -> inputTextCityErrorEmpty()
      else -> null
    },
    required = required,
    readOnly = readonly
  ) {
    width = 100.perc
    leadingIcon?.let { icon ->
      leadingIcon(icon.first).clickFlow.onEach {
        icon.second()
      }.launchIn(MainScope())
    }
    trailingIcon?.let { icon ->
      trailingIcon(icon.first).clickFlow.onEach {
        icon.second()
      }.launchIn(MainScope())
    }
  }

  fun updateState(nextValue: S, nextIsValid: Boolean) {
    state.update { current ->
      if (current.inputValue == nextValue && current.isValid == nextIsValid) current
      else current.copy(inputValue = nextValue, isValid = nextIsValid)
    }
  }

  fun updateValidity(isValid: Boolean) {
    state.update { current ->
      if (current.isValid == isValid) current
      else current.copy(isValid = isValid)
    }
  }

  fun applyRawValue(rawValue: String) {
    when (inputType) {
      MandarinInputType.BIRTHDATE -> {
        runCatching { LocalDate.parse(rawValue) }
          .onFailure { updateValidity(isValid = false) }
          .onSuccess { date ->
            updateState(nextValue = date.unsafeCast<S>(), nextIsValid = true)
          }
      }

      MandarinInputType.RESALE_START, MandarinInputType.RESALE_END -> {
        runCatching {
          LocalDateTime.parse(rawValue).toInstant(TimeZone.currentSystemDefault())
        }.onFailure {
          updateValidity(isValid = false)
        }.onSuccess { dateTime ->
          updateState(nextValue = dateTime.unsafeCast<S>(), nextIsValid = true)
        }
      }

      MandarinInputType.EMAIL -> {
        updateState(
          nextValue = rawValue.unsafeCast<S>(),
          nextIsValid = rawValue.matches(emailRegex)
        )
      }

      else -> {
        updateState(
          nextValue = rawValue.unsafeCast<S>(),
          nextIsValid = rawValue.isNotEmpty()
        )
      }
    }
  }

  fun readDomValue(): String {
    val hostElement = textField.getElementD()
    val shadowValue = hostElement?.shadowRoot
      ?.querySelector("input, textarea")
      ?.value
      ?.unsafeCast<String?>()

    return shadowValue ?: hostElement?.value?.unsafeCast<String?>().orEmpty()
  }

  fun reconcileFromDom() {
    applyRawValue(readDomValue())
  }

  fun scheduleReconcile(delayMillis: Long = 0L) {
    MainScope().launch {
      if (delayMillis > 0) delay(delayMillis.milliseconds)
      reconcileFromDom()
    }
  }

  textField.addAfterInsertHook {
    reconcileFromDom()
    scheduleReconcile(delayMillis = 150L)
  }

  textField.bind(isEnabled) { enabled ->
    disabled = enabled.not()
  }.bind(state) { input ->
    value = when (input.inputValue) {
      is String -> when (inputType) {
        MandarinInputType.BANK_ACCOUNT_IBAN -> input.inputValue.toFormattedIBANString()
        else -> input.inputValue
      }

      is String? -> when (inputType) {
        MandarinInputType.BANK_ACCOUNT_IBAN -> input.inputValue?.toFormattedIBANString().orEmpty()
        else -> input.inputValue.orEmpty()
      }

      is Instant -> when (inputType) {
        MandarinInputType.BIRTHDATE -> input.inputValue.toInputValue(DateTimeUtil.Type.DATE)
        MandarinInputType.RESALE_START, MandarinInputType.RESALE_END -> input.inputValue.roundOffToMinute()
          .toInputValue(DateTimeUtil.Type.DATETIME)

        else -> input.inputValue.roundOffToMinute().toInputValue(DateTimeUtil.Type.DATETIME)
      }

      is Amount -> input.inputValue.toPriceString()

      else -> input.inputValue.toString()
    }
    error = input.isValid.not()
    errorText = when (inputType) {
      MandarinInputType.FIRST_NAME -> inputTextFirstNameErrorEmpty()
      MandarinInputType.LAST_NAME -> inputTextLastNameErrorEmpty()
      MandarinInputType.EMAIL -> {
        if (input.inputValue is String && input.inputValue.isEmpty()) inputTextEmailErrorEmpty()
        else inputTextEmailErrorRegex()
      }

      MandarinInputType.PASSWORD -> inputTextPasswordErrorEmpty()
      MandarinInputType.BIRTHDATE -> inputDateBirthdateErrorEmpty()
      MandarinInputType.STREET -> inputTextStreetErrorEmpty()
      MandarinInputType.HOUSE_NUMBER -> inputTextHouseNumberErrorEmpty()
      MandarinInputType.POSTAL_CODE -> inputTextPostalCodeErrorEmpty()
      MandarinInputType.CITY -> inputTextCityErrorEmpty()
      else -> null
    }
  }.inputFlow.onEach { input ->
    applyRawValue(input.value)
  }.launchIn(MainScope())

  textField.changeFlow.onEach {
    reconcileFromDom()
  }.launchIn(MainScope())

  textField.eventFlow("blur").onEach {
    reconcileFromDom()
  }.launchIn(MainScope())

  textField.eventFlow("focus").onEach {
    scheduleReconcile(delayMillis = 75L)
    scheduleReconcile(delayMillis = 200L)
  }.launchIn(MainScope())
}
